/// <reference types="@angular/localize" />

import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouterModule } from '@angular/router';
import { tokenInterceptor } from '@lib/auth/shell';
import { translocoProvider } from '@lib/i18n/shell';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { DefaultDateRangeService } from '@platform-workspace/design-system';
import * as Sentry from '@sentry/angular';
import { DateAdapterService, DateParserService } from '@shared/data-access';
import { DEFAULT_DATE_FORMAT } from '@shared/utils/constants';
import { errorInterceptor } from '@shared/utils/interceptors';
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CustomFilterModule,
  DateFilterModule,
  EventApiModule,
  InfiniteRowModelModule,
  ModuleRegistry,
  NumberFilterModule,
  RowApiModule,
  RowSelectionModule,
  ScrollApiModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-community';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { appInitializer } from 'src/app.initializer';

import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { provideDatepickerActiveLanguage } from './app/providers/datepicker-active-language.provider';
import { provideDatePickerValueFormatter } from './app/providers/datepicker-value-formatter.provider';
import { provideDefaultErrorMessage } from './app/providers/default-error-messages.provider';
import { provideNotSelectedItemProvider } from './app/providers/not-selected-item-option.provider';
import { providePageSizeSelectorConfig, providePaginationBarConfig } from './app/providers/table-config.provider';
import { environment } from './environments/environment';

// Sentry is only initialised when a DSN is baked in at build time. `SENTRY_DSN` comes from
// `src/environments/build-env.const.ts`, generated from `.env` by `scripts/generate-env.mjs` - `process.env` is no
// longer substituted at build time now that the esbuild builder has replaced webpack + dotenv-webpack.
// import { SENTRY_DSN } from './environments/build-env.const';
// if (SENTRY_DSN) {
//   Sentry.init(browserOptions);
// }

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  ValidationModule,
  ColumnApiModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ScrollApiModule,
  RowSelectionModule,
  CellStyleModule,
  EventApiModule,
  RowApiModule,
  TooltipModule,
  ColumnAutoSizeModule,
  ClientSideRowModelApiModule,
]);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    translocoProvider(),
    importProvidersFrom(RouterModule.forRoot(APP_ROUTES), AngularSvgIconModule.forRoot()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: NgbDateAdapter,
      useClass: DateAdapterService,
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: DEFAULT_DATE_FORMAT,
      },
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.googleSiteKey },
    {
      provide: NgbDateParserFormatter,
      useClass: DateParserService,
    },
    DefaultDateRangeService,
    provideHttpClient(withInterceptors([errorInterceptor, tokenInterceptor])),
    provideAnimationsAsync(),
    provideAppInitializer(() => appInitializer()),
    provideDatePickerValueFormatter(),
    providePaginationBarConfig(),
    providePageSizeSelectorConfig(),
    provideDefaultErrorMessage(),
    provideNotSelectedItemProvider(),
    provideDatepickerActiveLanguage(),
  ],
});
